import { ApolloClient } from '@apollo/client';
// API
import {
  INIT_DELIVERABLE_FILE_MULTIPART_UPLOAD,
  COMPLETE_DELIVERABLE_FILE_MULTIPART_UPLOAD,
} from 'api/campaignV2/mutations';
// Types
import {
  InitDeliverableFileMultipartUpload,
  InitDeliverableFileMultipartUploadVariables,
} from 'api/campaignV2/types/InitDeliverableFileMultipartUpload';
import {
  CompleteDeliverableFileMultipartUpload,
  CompleteDeliverableFileMultipartUploadVariables,
} from 'api/campaignV2/types/CompleteDeliverableFileMultipartUpload';
import { AdminInitPostVideoMultipartUploadV2_adminInitPostVideoMultipartUploadV2 } from 'api/mediaPost/types/AdminInitPostVideoMultipartUploadV2';
// Helpers
import { Uploader } from 'helpers/uploader';
import { calculateChunkSize } from 'helpers/mediaFeed';
import { isSupportedVideoExtension } from 'helpers/videoFormats';

export type DeliverableFileUploadResult = {
  key: string;
  name: string;
  convertJobId?: string;
  needsConversion?: boolean;
};

export const normalizeConvertJobId = (
  convertJobIdResponse: unknown
): string | null => {
  if (!convertJobIdResponse) {
    return null;
  }

  if (typeof convertJobIdResponse === 'string') {
    const trimmed = convertJobIdResponse.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof convertJobIdResponse === 'object') {
    const id = (convertJobIdResponse as { id?: unknown }).id;
    if (typeof id === 'string') {
      const trimmed = id.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
  }

  return null;
};

export const ensureConvertJobIdForConvertedUpload = (
  file: File,
  convertJobIdResponse: unknown
): string | undefined => {
  const needsConversion = isFileNeedingConversion(file);
  const convertJobId = normalizeConvertJobId(convertJobIdResponse);

  if (needsConversion && !convertJobId) {
    throw new Error(
      `Upload completed for ${file.name}, but the backend did not return a convertJobId for a file that requires conversion.`
    );
  }

  return needsConversion ? convertJobId ?? undefined : undefined;
};

export const isMp4Video = (file: File): boolean => {
  if (file.type && !file.type.startsWith('video/')) {
    return false;
  }

  // The extension takes precedence over the MIME type: browsers report
  // video/mp4 for extensions that still need transcoding (e.g. Chromium
  // maps .m4v to video/mp4).
  const fileName = file.name.toLowerCase();
  if (/\.[^/.]+$/.test(fileName)) {
    return fileName.endsWith('.mp4');
  }

  return file.type === 'video/mp4';
};

export const isFileNeedingConversion = (file: File): boolean => {
  if (file.type.startsWith('image/')) {
    return false;
  }

  if (isMp4Video(file)) {
    return false;
  }

  if (file.type.startsWith('video/')) {
    return true;
  }

  // Browsers sometimes report an empty MIME type; fall back to the extension
  const extension = file.name.split('.').pop() ?? '';
  return !file.type && isSupportedVideoExtension(extension);
};

export const replaceExtensionWithMp4 = (
  fileName: string,
  file: File
): string => {
  const isVideo =
    file.type.startsWith('video/') ||
    /\.(mp4|mov|avi|webm|mkv|wmv|flv|3gp|m4v|mpg|mpeg|ts|mts|m2ts)$/i.test(
      fileName
    );

  if (!isVideo) {
    return fileName;
  }

  const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  return `${nameWithoutExt}.mp4`;
};

export const uploadDeliverableFileWithConversion = async (
  file: File,
  campaignId: string,
  deliverableItemId: string,
  client: ApolloClient<any>,
  onProgress?: (percentage: number) => void
): Promise<DeliverableFileUploadResult> => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'mp4';
  const contentType = file.type || 'video/mp4';
  const chunkSize = calculateChunkSize(file.size);
  const numberOfParts = Math.ceil(file.size / chunkSize);

  // Init multipart upload
  const { data: initData } = await client.mutate<
    InitDeliverableFileMultipartUpload,
    InitDeliverableFileMultipartUploadVariables
  >({
    mutation: INIT_DELIVERABLE_FILE_MULTIPART_UPLOAD,
    variables: {
      input: {
        campaignId,
        deliverableItemId,
        contentType,
        fileExt: fileExtension,
        partCnt: numberOfParts,
      },
    },
  });

  if (!initData?.initDeliverableFileMultipartUpload) {
    throw new Error('Failed to initialize multipart upload');
  }

  const initResponse = initData.initDeliverableFileMultipartUpload;

  const adaptedInitData = {
    fileId: initResponse.fileId,
    fileKey: initResponse.fileKey,
    signedUrls: initResponse.signedUrls.map((url) => ({
      signedUrl: url.signedUrl,
      partNumber: url.partNumber,
      __typename: 'GetMultipartPresignedUrlResponse' as const,
    })),
    __typename: 'InitMultipartUploadResponse' as const,
  } as AdminInitPostVideoMultipartUploadV2_adminInitPostVideoMultipartUploadV2;

  return new Promise((resolve, reject) => {
    const uploader = new Uploader({
      file,
      initData: adaptedInitData,
      chunkSize,
    });

    uploader.onProgress(({ percentage }) => {
      if (onProgress) {
        onProgress(percentage);
      }
    });

    uploader.onError((error) => {
      reject(error || new Error('Upload failed'));
    });

    uploader.onComplete(async (completeInput) => {
      try {
        const { data: completeData } = await client.mutate<
          CompleteDeliverableFileMultipartUpload,
          CompleteDeliverableFileMultipartUploadVariables
        >({
          mutation: COMPLETE_DELIVERABLE_FILE_MULTIPART_UPLOAD,
          variables: {
            input: completeInput,
          },
        });

        const needsConversion = isFileNeedingConversion(file);
        const finalConvertJobId = ensureConvertJobIdForConvertedUpload(
          file,
          completeData?.completeDeliverableFileMultipartUpload?.convertJobId
        );

        resolve({
          key: initResponse.fileKey,
          name: needsConversion
            ? replaceExtensionWithMp4(file.name, file)
            : file.name,
          convertJobId: finalConvertJobId,
          needsConversion,
        });
      } catch (error) {
        reject(error);
      }
    });

    uploader.start();
  });
};
